import { z } from 'zod';

/** Size / portion / variant price on a dish (e.g. 0,2l · 0,4l, klein · groß). */
export const MenuPriceOptionSchema = z.object({
  label: z.string().trim().max(64).default(''),
  priceCents: z.number().int().nonnegative(),
});

export type MenuPriceOption = z.infer<typeof MenuPriceOptionSchema>;

export const MenuPriceOptionsSchema = z.array(MenuPriceOptionSchema).max(8).default([]);

/** Generic words models sometimes put in `label` instead of leaving it empty. */
const GENERIC_PRICE_OPTION_LABELS = new Set([
  'price',
  'preis',
  'amount',
  'betrag',
  'cost',
  'kosten',
  'summe',
  'total',
]);

export function formatPriceCentsDisplay(cents: number): string {
  const amount = cents / 100;
  if (cents % 100 === 0) {
    return String(Math.trunc(amount));
  }
  return amount.toFixed(2).replace('.', ',');
}

export function parsePriceDisplayToCents(raw: string): number {
  const normalized = raw.replace(',', '.').trim();
  const parsed = Number.parseFloat(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return 0;
  }
  return Math.round(parsed * 100);
}

/**
 * Drop labels that are really the amount (or the word "price") so the size field
 * stays empty for the owner to fill — never mirror `priceCents` into `label`.
 */
export function sanitizePriceOptionLabel(label: string, priceCents: number): string {
  const trimmed = (label ?? '').trim().slice(0, 64);
  if (!trimmed) {
    return '';
  }
  if (GENERIC_PRICE_OPTION_LABELS.has(trimmed.toLowerCase())) {
    return '';
  }

  const amount = formatPriceCentsDisplay(priceCents);
  const euro = priceCents / 100;
  const candidates = new Set(
    [
      amount,
      amount.replace(',', '.'),
      String(Math.trunc(euro)),
      euro.toFixed(2),
      euro.toFixed(2).replace('.', ','),
      String(priceCents),
    ].map((value) => value.toLowerCase()),
  );
  const stripped = trimmed.replace(/[€$£\s]/g, '').toLowerCase();
  if (candidates.has(trimmed.toLowerCase()) || candidates.has(stripped)) {
    return '';
  }
  return trimmed;
}

/**
 * Canonical editor/storage shape: always ≥1 option.
 * Legacy `priceCents` alone (no options) becomes `[{ label: '', priceCents }]`.
 */
export function normalizeMenuPriceOptions(
  priceCents: number,
  priceOptions?: MenuPriceOption[] | null,
): MenuPriceOption[] {
  const cleaned = (priceOptions ?? [])
    .filter((option): option is MenuPriceOption => option != null)
    .map((option) => {
      const nextCents =
        Number.isFinite(option.priceCents) && option.priceCents >= 0
          ? Math.trunc(option.priceCents)
          : 0;
      return {
        label: sanitizePriceOptionLabel(option.label ?? '', nextCents),
        priceCents: nextCents,
      };
    });

  if (cleaned.length === 0) {
    return [{ label: '', priceCents: Math.max(0, Math.trunc(priceCents) || 0) }];
  }
  return cleaned;
}

/**
 * Persist priceOptions only — always keep at least one row.
 * Dish-level `priceCents` in DB/API is derived from options[0], never a second source of truth.
 */
export function serializeMenuPriceOptions(
  priceCents: number,
  priceOptions?: MenuPriceOption[] | null,
): MenuPriceOption[] {
  return normalizeMenuPriceOptions(priceCents, priceOptions);
}

export function primaryPriceCentsFromOptions(
  priceCents: number,
  priceOptions?: MenuPriceOption[] | null,
): number {
  const normalized = normalizeMenuPriceOptions(priceCents, priceOptions);
  return normalized[0]?.priceCents ?? Math.max(0, Math.trunc(priceCents) || 0);
}

/** Guest-facing price string for CDL / site blocks. */
export function formatMenuItemPriceDisplay(
  priceCents: number,
  priceOptions?: MenuPriceOption[] | null,
): string {
  const options = normalizeMenuPriceOptions(priceCents, priceOptions);
  if (options.length === 1 && !options[0]!.label) {
    return formatPriceCentsDisplay(options[0]!.priceCents);
  }
  return options
    .map((option) => {
      const amount = formatPriceCentsDisplay(option.priceCents);
      return option.label ? `${option.label} ${amount}` : amount;
    })
    .join(' / ');
}
